// The admin panel's one door to the database: Firestore, the `mobilistan` database in Doha
// (src/firebase.js). Collection reads return { id: data } — or null when empty — so every screen
// handles results the same way.
//
// Until Sept 2026 each collection was routed to Firestore or the old Realtime Database (Iowa) by a
// table here, and a collection missing from the table silently went to the Realtime Database, where
// nothing was allowed: petty cash, cost centres, call logs, stock counts, Instant offers and
// recovery assignments never saved. Everything is Firestore now, so there is no table to forget.
import {
  collection as fsCol,
  doc as fsDoc,
  onSnapshot,
  getDocs,
  getDoc as fsGetDoc,
  setDoc as fsSetDoc,
  addDoc,
  updateDoc as fsUpdateDoc,
  deleteDoc,
  query as fsQuery,
  where,
  orderBy,
  increment,
  runTransaction as fsRunTransaction,
} from 'firebase/firestore';
import { firestore } from '../firebase';

function snapToObj(querySnap) {
  const out = {};
  querySnap.forEach(d => { out[d.id] = d.data(); });
  return Object.keys(out).length ? out : null;
}

// A VOIDED PAYMENT IS NOT MONEY, ANYWHERE.
//
// Six August entries were keyed twice and credited six customers with 29,750 they
// had never paid. Voiding rather than deleting keeps the receipt, the collector
// and the date — a deleted row explains nothing to whoever asks next month.
//
// But `voided` was honoured in exactly two files, the balance and the statement.
// Thirty other places read `payments` and add up amounts: today's collections,
// the month's recovery figures, the admin reports. Every one of them would have
// gone on counting money that was never received, so a customer's own ledger and
// the company's collection report would disagree by 29,750 with nothing to
// explain the difference.
//
// Filtering here rather than in thirty callers is deliberate: thirty edits is
// thirty chances to miss one, and the next screen written would have missed it
// too. This is the one door they all come through.
//
// Voided rows are still IN Firestore and still readable — getDoc() returns them
// untouched, and an audit view wanting the history should read them that way.
// Only the collection reads, the ones that add up, drop them.
function dropVoided(col, obj) {
  if (col !== 'payments' || !obj) return obj;
  const out = {};
  Object.entries(obj).forEach(([id, v]) => { if (!v || v.voided !== true) out[id] = v; });
  return Object.keys(out).length ? out : null;
}

const docOf = (...path) => fsDoc(firestore, ...path);

// A listener that fails (permission, missing index, offline edge cases) stops for good. Log it once
// with the collection name instead of an anonymous "Uncaught Error in snapshot listener".
const onListenError = (what) => (err) => {
  console.warn(`[db] listener on ${what} stopped:`, err && (err.code || err.message || err));
};

const db = {
  subscribe(col, callback) {
    return onSnapshot(fsCol(firestore, col), snap => callback(dropVoided(col, snapToObj(snap))), onListenError(col));
  },

  subscribeDoc(col, id, callback) {
    return onSnapshot(docOf(col, id), snap =>
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onListenError(`${col}/${id}`));
  },

  subscribeQuery(col, filters, callback) {
    // Firestore throws on an empty 'in' / 'array-contains-any' list (e.g. a manager with no city):
    // nothing can match, so answer "no results" instead of crashing the screen.
    if ((filters || []).some(([, op, value]) => (op === 'in' || op === 'array-contains-any') && (!Array.isArray(value) || value.length === 0))) {
      callback(null);
      return () => {};
    }
    const clauses = (filters || []).map(([field, op, value]) => where(field, op, value));
    const q = fsQuery(fsCol(firestore, col), ...clauses);
    return onSnapshot(q, snap => callback(dropVoided(col, snapToObj(snap))), onListenError(col));
  },

  getAll(col) {
    return getDocs(fsCol(firestore, col)).then(snap => dropVoided(col, snapToObj(snap)));
  },

  getQuery(col, filters) {
    const clauses = (filters || []).map(([field, op, value]) => where(field, op, value));
    return getDocs(fsQuery(fsCol(firestore, col), ...clauses)).then(snap => dropVoided(col, snapToObj(snap)));
  },

  getDoc(col, id) {
    return fsGetDoc(docOf(col, id)).then(snap =>
      snap.exists() ? { id: snap.id, ...snap.data() } : null
    );
  },

  // options: { merge: true } creates the document when absent and merges into it
  // when present, leaving sibling fields alone. Without it this REPLACES the whole
  // document — which is the right default for a full write, and data loss for a
  // partial one.
  setDoc(col, id, data, options) {
    return options && options.merge
      ? fsSetDoc(docOf(col, id), data, { merge: true })
      : fsSetDoc(docOf(col, id), data);
  },

  pushDoc(col, data) {
    return addDoc(fsCol(firestore, col), data).then(r => r.id);
  },

  updateDoc(col, id, data) {
    return fsUpdateDoc(docOf(col, id), data);
  },

  removeDoc(col, id) {
    return deleteDoc(docOf(col, id));
  },

  counterIncrement(col, id, field, amount = 1) {
    return fsUpdateDoc(docOf(col, id), { [field]: increment(amount) });
  },

  pushSubdoc(col, docId, subcol, data) {
    return addDoc(fsCol(firestore, col, docId, subcol), data).then(r => r.id);
  },

  updateSubdoc(col, docId, subcol, subdocId, data) {
    return fsUpdateDoc(docOf(col, docId, subcol, subdocId), data);
  },

  setSubdoc(col, docId, subcol, subdocId, data) {
    return fsSetDoc(docOf(col, docId, subcol, subdocId), data);
  },

  removeSubdoc(col, docId, subcol, subdocId) {
    return deleteDoc(docOf(col, docId, subcol, subdocId));
  },

  subscribeSubcol(col, docId, subcol, callback) {
    return onSnapshot(fsCol(firestore, col, docId, subcol), snap => callback(snapToObj(snap)), onListenError(`${col}/${docId}/${subcol}`));
  },

  // Atomic read-modify-write on a single document. The updateFn receives the
  // current value (or null) and must return the next value to write. Used for
  // counters or merge-style updates where concurrent writers could otherwise
  // stomp each other.
  dbTransaction(col, id, updateFn) {
    return fsRunTransaction(firestore, async (t) => {
      const docRef = docOf(col, id);
      const snap = await t.get(docRef);
      const current = snap.exists() ? snap.data() : null;
      t.set(docRef, updateFn(current));
    });
  },
};

export { snapToObj, orderBy };
export default db;
